import React, { useRef, useState } from 'react';
import { toPng } from 'html-to-image';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Phone, Mail, Globe, MapPin, Download, Copy, CheckCircle2 } from 'lucide-react';
import logger from '../utils/logger';

const BRAND_GREEN = '#16a34a';
const BRAND_BLUE = '#2563eb';

// DIGI-LOANS official logo (provided by user)
const DigiLoansLogo = ({ size = 96 }) => (
    <img
        src="/digi-loans-logo.png"
        alt="DIGI-LOANS logo"
        width={size}
        height={size}
        style={{ width: size, height: size, objectFit: 'contain', display: 'block' }}
    />
);

const VisitingCard = ({ rm, role = 'Relationship Manager', companyAddress = '8 Camac Street, Shantiniketan Building,\n5th Floor, Room No 509,\nKolkata 700017', companyEmail = 'operation@digi-loan.com', companyWebsite = 'https://www.digi-loan.com' }) => {
    const cardRef = useRef(null);
    const [downloading, setDownloading] = useState(false);
    const [copied, setCopied] = useState(false);

    const phone = rm?.phone || '9830480366';
    const email = rm?.email || companyEmail;
    const name = rm?.name || 'RM Name';
    const designation = rm?.designation || role;
    const employeeCode = rm?.employee_id || '';

    const downloadPng = async () => {
        if (!cardRef.current) return;
        setDownloading(true);
        try {
            const dataUrl = await toPng(cardRef.current, { cacheBust: true, backgroundColor: '#ffffff', pixelRatio: 3, style: { fontFamily: 'Inter, sans-serif' } });
            const link = document.createElement('a');
            link.download = `${name.replace(/\s+/g, '-').toLowerCase()}-visiting-card.png`;
            link.href = dataUrl;
            link.click();
        } catch (err) { logger.error('vcard download', err); alert('Download failed: ' + err.message); }
        finally { setDownloading(false); }
    };

    const shareText = encodeURIComponent(
        `📇 *${name}* — ${designation}${employeeCode ? `\n🆔 Employee Code: ${employeeCode}` : ''}\n📞 ${phone}\n✉️ ${email}\n🌐 ${companyWebsite}\n📍 ${companyAddress.replace(/\n/g, ', ')}\n\n— DIGI-LOANS · Loan Simplified`
    );

    const copyContact = async () => {
        try {
            await navigator.clipboard.writeText(decodeURIComponent(shareText));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch { alert('Copy failed'); }
    };

    const shareUrls = {
        whatsapp: `https://wa.me/?text=${shareText}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(companyWebsite)}&summary=${shareText}`,
        twitter: `https://twitter.com/intent/tweet?text=${shareText}`,
        telegram: `https://t.me/share/url?url=${encodeURIComponent(companyWebsite)}&text=${shareText}`,
        email: `mailto:?subject=${encodeURIComponent(`Contact: ${name} - DIGI-LOANS`)}&body=${shareText}`,
    };

    const openShare = (platform) => window.open(shareUrls[platform], '_blank', 'noopener,noreferrer');

    return (
        <div className="space-y-3">
            {/* The Visiting Card itself — exported as PNG */}
            <Card ref={cardRef} className="relative overflow-hidden border border-slate-300 shadow-md bg-white" style={{ aspectRatio: '1.75 / 1', maxWidth: 640 }} data-testid="visiting-card">
                <CardContent className="p-0 h-full">
                    <div className="grid grid-cols-[44%_1px_1fr] h-full">
                        {/* LEFT — Logo (stacked wordmark + tagline baked in) */}
                        <div className="flex items-center justify-center p-4 bg-gradient-to-br from-emerald-50/40 via-white to-blue-50/40">
                            <DigiLoansLogo size={200} />
                        </div>

                        {/* Vertical Divider */}
                        <div className="bg-slate-300 h-3/4 self-center" />

                        {/* RIGHT — RM Info */}
                        <div className="p-5 flex flex-col justify-center space-y-2.5">
                            <div>
                                <p className="font-black leading-tight" style={{ fontFamily: 'Chivo, sans-serif', color: BRAND_GREEN, fontSize: 20 }}>{name}</p>
                                <p className="text-sm text-slate-600 font-medium">{designation}</p>
                                {employeeCode && (
                                    <span
                                        className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider tabular-nums"
                                        style={{ background: `${BRAND_BLUE}15`, color: BRAND_BLUE, border: `1px solid ${BRAND_BLUE}40`, fontFamily: 'JetBrains Mono, ui-monospace, SFMono-Regular, monospace' }}
                                        data-testid="vcard-employee-code"
                                    >
                                        ID · {employeeCode}
                                    </span>
                                )}
                            </div>
                            <div className="space-y-2 mt-1">
                                <div className="flex items-center gap-2.5">
                                    <span className="flex items-center justify-center w-6 h-6" style={{ backgroundColor: BRAND_GREEN }}><Phone className="h-3.5 w-3.5 text-white" /></span>
                                    <span className="text-sm text-slate-800 font-medium tabular-nums">{phone}</span>
                                </div>
                                <div className="flex items-center gap-2.5">
                                    <span className="flex items-center justify-center w-6 h-6" style={{ backgroundColor: BRAND_BLUE }}><Mail className="h-3.5 w-3.5 text-white" /></span>
                                    <span className="text-sm text-slate-800 font-medium break-all">{email}</span>
                                </div>
                                <div className="flex items-center gap-2.5">
                                    <span className="flex items-center justify-center w-6 h-6" style={{ backgroundColor: BRAND_GREEN }}><Globe className="h-3.5 w-3.5 text-white" /></span>
                                    <span className="text-sm text-slate-800 font-medium">{companyWebsite.replace('https://', '')}</span>
                                </div>
                                <div className="flex items-start gap-2.5">
                                    <span className="flex items-center justify-center w-6 h-6 mt-0.5 flex-shrink-0" style={{ backgroundColor: BRAND_GREEN }}><MapPin className="h-3.5 w-3.5 text-white" /></span>
                                    <span className="text-xs text-slate-700 leading-tight whitespace-pre-line">{companyAddress}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Bottom decorative stripes — green + blue */}
                    <div className="absolute bottom-0 left-0 right-0 flex h-3">
                        <div className="flex-1" style={{ backgroundColor: BRAND_GREEN, clipPath: 'polygon(0 0, 95% 0, 100% 100%, 0 100%)' }} />
                        <div className="flex-1" style={{ backgroundColor: BRAND_BLUE, clipPath: 'polygon(5% 0, 100% 0, 100% 100%, 0 100%)', marginLeft: -20 }} />
                    </div>
                </CardContent>
            </Card>

            {/* Action toolbar */}
            <div className="flex flex-wrap items-center gap-2">
                <Button onClick={downloadPng} disabled={downloading} className="rounded-none" style={{ backgroundColor: BRAND_BLUE }} data-testid="download-vcard-btn">
                    <Download className="h-4 w-4 mr-2" />{downloading ? 'Generating…' : 'Download PNG'}
                </Button>
                <Button variant="outline" onClick={copyContact} className="rounded-none" data-testid="copy-vcard-btn">
                    {copied ? <><CheckCircle2 className="h-4 w-4 mr-2 text-emerald-600" />Copied!</> : <><Copy className="h-4 w-4 mr-2" />Copy contact</>}
                </Button>

                <div className="flex items-center gap-1 ml-1 border border-slate-200 px-1 py-1">
                    <span className="text-[10px] uppercase font-bold text-slate-400 px-2">Share:</span>
                    {/* WhatsApp */}
                    <button onClick={() => openShare('whatsapp')} className="h-8 w-8 flex items-center justify-center bg-[#25D366] hover:opacity-90 transition-opacity" title="WhatsApp" data-testid="share-whatsapp">
                        <svg viewBox="0 0 24 24" className="h-4 w-4 text-white fill-current"><path d="M17.498 14.382c-.301-.15-1.767-.867-2.04-.966-.273-.101-.473-.15-.673.15-.197.295-.771.964-.944 1.162-.175.195-.349.21-.646.075-.3-.15-1.263-.465-2.403-1.485-.888-.795-1.484-1.77-1.66-2.07-.174-.3-.019-.465.13-.615.136-.135.301-.345.451-.523.146-.181.194-.301.297-.496.1-.21.049-.375-.025-.524-.075-.15-.672-1.62-.922-2.206-.24-.584-.487-.51-.672-.51-.172-.015-.371-.015-.571-.015-.2 0-.523.074-.797.359-.273.3-1.045 1.02-1.045 2.475s1.07 2.865 1.219 3.075c.149.195 2.105 3.195 5.1 4.485.714.3 1.27.48 1.704.629.714.227 1.365.195 1.88.121.574-.091 1.767-.721 2.016-1.426.255-.705.255-1.29.18-1.425-.074-.135-.27-.21-.57-.345m-5.446 7.443h-.016c-1.77 0-3.524-.48-5.055-1.38l-.36-.214-3.75.975 1.005-3.645-.239-.375a9.869 9.869 0 0 1-1.516-5.26c0-5.445 4.455-9.885 9.942-9.885 2.654 0 5.145 1.035 7.021 2.91 1.875 1.859 2.909 4.35 2.909 6.99-.004 5.444-4.46 9.885-9.935 9.885M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.334.101 11.893c0 2.096.549 4.14 1.595 5.945L0 24l6.335-1.652a12.062 12.062 0 0 0 5.71 1.447h.006c6.585 0 11.946-5.336 11.949-11.896 0-3.176-1.24-6.165-3.495-8.411"/></svg>
                    </button>
                    {/* LinkedIn */}
                    <button onClick={() => openShare('linkedin')} className="h-8 w-8 flex items-center justify-center bg-[#0A66C2] hover:opacity-90 transition-opacity" title="LinkedIn" data-testid="share-linkedin">
                        <svg viewBox="0 0 24 24" className="h-4 w-4 text-white fill-current"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                    </button>
                    {/* Twitter / X */}
                    <button onClick={() => openShare('twitter')} className="h-8 w-8 flex items-center justify-center bg-black hover:opacity-90 transition-opacity" title="X (Twitter)" data-testid="share-twitter">
                        <svg viewBox="0 0 24 24" className="h-4 w-4 text-white fill-current"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    </button>
                    {/* Telegram */}
                    <button onClick={() => openShare('telegram')} className="h-8 w-8 flex items-center justify-center bg-[#229ED9] hover:opacity-90 transition-opacity" title="Telegram" data-testid="share-telegram">
                        <svg viewBox="0 0 24 24" className="h-4 w-4 text-white fill-current"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
                    </button>
                    {/* Email */}
                    <button onClick={() => openShare('email')} className="h-8 w-8 flex items-center justify-center bg-slate-700 hover:opacity-90 transition-opacity" title="Email" data-testid="share-email">
                        <Mail className="h-4 w-4 text-white" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default VisitingCard;
